import React, { useCallback, useEffect, useRef, useState } from 'react';
import TrashIcon from '../../icon/TrashIcon';
import classNames from 'classnames';
import { px2rem } from '../../../core';

export interface Props {
    onDelete: () => void;
    onActive?: (isActive: boolean) => void;
    disabled?: boolean;
    height?: number;
    transitionDuration?: number;
    deleteWidth?: number;
    deleteColor?: string;
    deleteText?: string;
    className?: string;
    children?: React.ReactNode;
}

const cursorPosition = (event: TouchEvent | MouseEvent | React.TouchEvent | React.MouseEvent) => {
    if (event instanceof TouchEvent) return event.touches[0].clientX;
    if (event instanceof MouseEvent) return event.clientX;
    if (event.nativeEvent instanceof TouchEvent) return event.nativeEvent.touches[0].clientX;
    return event.nativeEvent.clientX;
};

const SwipeToDelete = ({ onDelete, disabled = false, deleteWidth = 60, className = '', children, onActive }: Props) => {
    const [touching, setTouching] = useState(false);
    const [translate, setTranslate] = useState(0);
    const [deleting, setDeleting] = useState(false);

    const startTouchPosition = useRef(0);
    const initTranslate = useRef(0);
    const container = useRef<HTMLDivElement>(null);
    const containerWidth: number = container.current?.getBoundingClientRect().width || 0;
    const deleteWithoutConfirmThreshold: number = containerWidth * 0.75;

    useEffect(() => {
        if (disabled) {
            setTranslate(0);
        }
    }, [disabled]);

    const onStart = useCallback(
        (event: React.TouchEvent | React.MouseEvent) => {
            if (disabled) return;
            if (touching) return;

            startTouchPosition.current = cursorPosition(event);
            initTranslate.current = translate;
            setTouching(true);
        },
        [disabled, touching, translate],
    );

    const onMove = useCallback(
        function (event: TouchEvent | MouseEvent) {
            if (!touching) return;
            if (disabled) return;
            if (cursorPosition(event) > startTouchPosition.current - initTranslate.current) return setTranslate(0);
            const translate = cursorPosition(event) - startTouchPosition.current + initTranslate.current;
            setTranslate(translate);
        },
        [touching, disabled],
    );

    const onMouseMove = useCallback(
        (event: MouseEvent) => {
            onMove(event);
        },
        [onMove],
    );

    const onTouchMove = useCallback(
        (event: TouchEvent) => {
            onMove(event);
        },
        [onMove],
    );
    const onMouseUp = useCallback(
        function () {
            startTouchPosition.current = 0;
            const acceptableMove = -deleteWidth * 0.7;
            const showDelete = translate < acceptableMove;
            if (showDelete) {
                setTranslate(-1 * deleteWidth);
                onActive && onActive(true);
            } else {
                setTranslate(0);
                onActive && onActive(false);
            }
            setTouching(() => false);
        },
        [containerWidth, deleteWidth, deleteWithoutConfirmThreshold, translate],
    );

    useEffect(() => {
        if (touching) {
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('touchmove', onTouchMove);
            window.addEventListener('mouseup', onMouseUp);
            window.addEventListener('touchend', onMouseUp);
        } else {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('mouseup', onMouseUp);
            window.removeEventListener('touchend', onMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('mouseup', onMouseUp);
            window.removeEventListener('touchend', onMouseUp);
        };
    }, [onMouseMove, onMouseUp, onTouchMove, touching]);

    return (
        <div className={`relative ${deleting ? ' deleting' : ''} ${className}`} ref={container}>
            <div
                className={`absolute right-1 left-1 top-1 bottom-1 bg-error flex items-center justify-end text-white text-16`}
            >
                {/* fix scale screen */}
                <button className={`w-60`} onClick={() => onDelete()}>
                    <TrashIcon />
                </button>
            </div>
            <div
                className={classNames([`h-full relative transform`])}
                style={{ transform: `translateX(${px2rem(translate)})`, transition: 'transform 250ms ease-out 0s' }}
                onMouseDown={onStart}
                onTouchStart={onStart}
            >
                {children}
            </div>
        </div>
    );
};

export default SwipeToDelete;
